import React from 'react';
import { ClientBriefingState, SubscribeClientBriefing } from '../interfaces';
import { Theme } from '../Theme';

type RequestBriefing = (clientId: string, force?: boolean) => Promise<{ status: string }>;
const EMPTY_STATE: ClientBriefingState = { briefing: null, loading: false };

// Replace transport error codes with useful actions while preserving specific application messages.
function formatClientBriefingError(error: unknown, fallback: string): string {
    const details = error && typeof error === 'object' ? error as { code?: unknown; message?: unknown } : {};
    const message = (typeof error === 'string' ? error : typeof details.message === 'string' ? details.message : '').trim();
    const embeddedCode = message.match(/\((?:functions|auth|firestore)\/([a-z-]+)\)/i)?.[1];
    const code = ((typeof details.code === 'string' ? details.code.split('/').pop() : embeddedCode) || '').toLowerCase();
    const normalizedMessage = message.replace(/^Firebase(?:Error)?:\s*/i, '')
        .replace(/\s*\((?:functions|auth|firestore)\/[^)]+\)\.?$/i, '')
        .replace(/^(?:functions|auth|firestore)\//i, '').replace(/[.!]+$/, '').trim().toLowerCase();
    const genericMessages = new Set([
        '', 'error', 'unknown', 'internal', 'internal error', 'an internal error has occurred',
        'unavailable', 'deadline-exceeded', 'deadline exceeded', 'unauthenticated',
        'network-request-failed', 'network error', 'network request failed', 'failed to fetch',
        'networkerror when attempting to fetch resource', 'load failed',
    ]);
    if (!genericMessages.has(normalizedMessage)) {
        return message;
    }
    const isNetworkError = ['network error', 'network request failed', 'failed to fetch',
        'networkerror when attempting to fetch resource', 'load failed'].includes(normalizedMessage);
    const friendlyMessages: Record<string, string> = {
        internal: 'Could not prepare the Client summary. Please try again.',
        unavailable: 'The summary service is temporarily unavailable. Please try again shortly.',
        'deadline-exceeded': 'The summary request timed out. Please try again.',
        'network-request-failed': 'Could not connect to the summary service. Check your connection and try again.',
        unauthenticated: 'Please sign in again and reopen this Client record to load its summary.',
    };
    return friendlyMessages[code || (isNetworkError ? 'network-request-failed' : normalizedMessage.replace(/ /g, '-'))] || fallback;
}

// Keep the selected record's subscription alive across tab changes, with no stale cross-record state.
export function useClientBriefing(options: {
    clientId: string;
    providerId: string;
    userId: string;
    enabled: boolean;
    subscribe?: SubscribeClientBriefing;
    request?: RequestBriefing;
}) {
    const { clientId, providerId, userId, enabled, subscribe, request } = options;
    const scope = JSON.stringify([clientId, providerId, userId, enabled]);
    const [snapshot, setSnapshot] = React.useState({ scope, state: EMPTY_STATE });
    const [requestState, setRequestState] = React.useState({ scope, pending: false, error: '', status: '' });
    const generation = React.useRef(0);
    const retrySubscription = React.useRef<() => void>(() => {});
    const callbacks = React.useRef({ subscribe, request });
    callbacks.current = { subscribe, request };

    React.useEffect(() => {
        const currentGeneration = ++generation.current;
        let unsubscribe: (() => void) | undefined;
        let active = true;
        setSnapshot({ scope, state: { briefing: null, loading: enabled } });
        setRequestState({ scope, pending: false, error: '', status: '' });
        // Listener callbacks may arrive after cleanup, so identity alone is insufficient.
        const connect = () => {
            unsubscribe?.();
            try {
                unsubscribe = callbacks.current.subscribe?.(clientId, (state) => {
                    if (active && currentGeneration === generation.current) {
                        setSnapshot({ scope, state: { ...state, error: state.error ? formatClientBriefingError(state.error, 'Could not load the Client summary. Please try again.') : undefined } });
                        if (!state.loading) {
                            setRequestState((previous) => ({ ...previous, status: '' }));
                        }
                    }
                });
            } catch (error) {
                setSnapshot({ scope, state: { briefing: null, loading: false, error: formatClientBriefingError(error, 'Could not load the Client summary. Please try again.') } });
            }
        };
        retrySubscription.current = connect;
        if (enabled && clientId && callbacks.current.subscribe && callbacks.current.request) {
            connect();
            setRequestState({ scope, pending: true, error: '', status: '' });
            void callbacks.current.request(clientId).then((result) => {
                if (active && currentGeneration === generation.current) {
                    setRequestState({ scope, pending: false, error: '', status: result.status });
                }
            }).catch((error) => {
                if (active && currentGeneration === generation.current) {
                    setRequestState({ scope, pending: false, error: formatClientBriefingError(error, 'Could not prepare the Client summary. Please try again.'), status: '' });
                }
            });
        } else {
            setSnapshot({ scope, state: EMPTY_STATE });
        }
        return () => {
            active = false;
            generation.current += 1;
            unsubscribe?.();
            retrySubscription.current = () => {};
        };
    }, [clientId, scope, enabled]);

    const state = snapshot.scope === scope && enabled ? snapshot.state : EMPTY_STATE;
    const currentRequest = requestState.scope === scope ? requestState : { pending: false, error: '', status: '' };

    // Retry reconnects a failed listener as well as requesting a fresh server generation.
    const refresh = async () => {
        if (!enabled || !callbacks.current.request || currentRequest.pending) {
            return;
        }
        const currentGeneration = generation.current;
        if (state.error) {
            retrySubscription.current();
        }
        setRequestState({ scope, pending: true, error: '', status: '' });
        try {
            const result = await callbacks.current.request(clientId, true);
            if (generation.current === currentGeneration) {
                setRequestState({ scope, pending: false, error: '', status: result.status });
            }
        } catch (error) {
            if (generation.current === currentGeneration) {
                setRequestState({ scope, pending: false, error: formatClientBriefingError(error, 'Could not refresh the Client summary. Please try again.'), status: '' });
            }
        }
    };
    return { state, requestPending: currentRequest.pending, requestError: currentRequest.error, requestedStatus: currentRequest.status, refresh };
}

// Present the briefing as readable prose with explicit coverage notices.
export function ClientBriefingPanel(props: {
    state: ClientBriefingState;
    requestPending?: boolean;
    requestError?: string;
    requestedStatus?: string;
    onRefresh?: () => void;
    canViewBriefingDiagnostics?: boolean;
}) {
    const { state, requestPending, requestError, requestedStatus, onRefresh, canViewBriefingDiagnostics = false } = props;
    const { briefing, loading } = state;
    const sections = briefing?.sections || [];
    const status = requestedStatus === 'queued' || requestedStatus === 'processing' ? requestedStatus : briefing?.status;
    const updating = !!requestPending || status === 'queued' || status === 'processing';
    const rawError = state.error || requestError || (briefing?.status === 'error' ? briefing.error || 'The summary could not be generated.' : '');
    const error = rawError ? formatClientBriefingError(rawError, 'Could not prepare the Client summary. Please try again.') : '';
    // Queued jobs can wait for the next processing allowance; retry cannot bypass that wait.
    const processingNotice = briefing?.status === 'queued' || briefing?.status === 'processing' ? briefing.error?.trim() : '';
    // Keep each affected source visible while removing an identical inherited generic warning.
    const sourceWarnings = briefing?.unavailableSources || [];
    const namedWarningReasons = new Set(sourceWarnings.filter((source) => !!source.sourceId).map((source) => source.reason));
    const unavailable = sourceWarnings.filter((source) => !!source.sourceId || !namedWarningReasons.has(source.reason));
    const hasPartialInformation = briefing?.status === 'partial' || unavailable.length > 0;

    return (
        <div style={styles.content}>
            {/* Keep the summary action available without introductory metadata lines. */}
            {onRefresh && <div style={styles.toolbar}>
                <button type='button' style={styles.button} onClick={onRefresh} disabled={updating || loading}>
                    {error ? 'Retry summary' : 'Refresh summary'}
                </button>
            </div>}
            {(loading || updating || briefing?.isStale) && <div role='status' style={styles.notice}>
                {loading ? 'Loading summary…' : updating ?
                    (sections.length ? `${processingNotice ? 'Summary update is waiting.' : 'Updating summary.'} The previous briefing is shown below. It may not include recent changes.` : 'Preparing summary from the Client record and Timeline…') :
                    'This summary needs updating.'}
                {!!briefing?.pendingSourceCount && ` ${briefing.pendingSourceCount} source${briefing.pendingSourceCount === 1 ? '' : 's'} ${processingNotice ? 'remaining' : 'still processing'}.`}
                {updating && !sections.length && ' Large histories and attachments are processed in stages.'}
                {processingNotice && <div style={styles.processingNotice}>{processingNotice}</div>}
            </div>}
            {error && <div role='alert' style={styles.warning}>{error}</div>}
            {/* Technical source diagnostics are reserved for root users; other staff see a concise coverage notice. */}
            {hasPartialInformation && (canViewBriefingDiagnostics ? (
                <div style={styles.warning}>
                    <strong>Some information is missing from this summary.</strong>
                    {unavailable.length > 0 && <ul style={styles.warnings}>{unavailable.map((source, index) => (
                        <li key={`${source.sourceId}:${source.reason}:${index}`}>{source.title}: {source.reason}</li>
                    ))}</ul>}
                </div>
            ) : (
                <p style={styles.partialNotice}>Summary based on partial information. Check the Timeline for the full record.</p>
            ))}
            {sections.map((section, index) => <section key={`${index}:${section.title}`}>
                <h3 style={styles.heading}>{section.title}</h3>
                <p style={styles.text}>{section.text}</p>
            </section>)}
            {!sections.length && !loading && !updating && !error && !hasPartialInformation && <p style={styles.text}>
                No summary is available yet. Add relevant information to the Client record or Timeline to build their story.
            </p>}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    content: { ...Theme.Fonts.body, padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '14px', lineHeight: 1.5 },
    toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' },
    button: { font: 'inherit', fontSize: '13px', padding: '6px 10px', border: '1px solid #d7d9df', borderRadius: '6px', background: 'white', color: Theme.Colours.text, cursor: 'pointer' },
    notice: { color: '#46536d', background: '#f1f5fa', padding: '10px 12px', borderRadius: '6px' },
    processingNotice: { marginTop: '6px' },
    warning: { color: '#725415', background: '#fff8e8', padding: '10px 12px', borderRadius: '6px' },
    partialNotice: { margin: 0, color: '#5b6068', fontSize: '13px' },
    warnings: { margin: '6px 0 0', paddingLeft: '22px' },
    heading: { fontSize: '16px', margin: '0 0 6px', fontWeight: 600 },
    text: { margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
};
